import { Component, inject } from '@angular/core';
import { ButtonModule } from "primeng/button";
import { AvatarModule } from "primeng/avatar";
import { ToolbarModule } from "primeng/toolbar";
import { CardModule } from "primeng/card";
import { CommonModule } from '@angular/common';
import { UserModel } from '@tsin-core/models/auth.model';
import { AuthState } from '@tsin-core/states/auth.state';
import { Observable, take } from 'rxjs';
import { Store } from '@ngxs/store';
import { LogoutAction } from '@tsin-core/actions/auth.action';
import { environment } from '@tsin-env/environment';
import { Router } from '@angular/router';
import { ConfigService } from '@tsin-core/services/common/config.service';
import { CognitoAuthService } from '@tsin-core/services/auth/cognito-auth.service';
import { signOut } from 'aws-amplify/auth';
import { AppLocale, LocaleService } from '@tsin-core/services/common/locale.service';
import { TranslatePipe } from '@ngx-translate/core';
import { isStaffOrAdminRole } from '@tsin-shared/utils/utils';
import { map } from 'rxjs';

@Component({
    selector: 'app-main-header',
    imports: [CommonModule, AvatarModule, ButtonModule, ToolbarModule, CardModule, TranslatePipe],
    templateUrl: './main-header.component.html',
    styleUrl: './main-header.component.scss'
})
export class MainHeaderComponent {

  store: Store = inject(Store);
  router: Router = inject(Router);
  configService: ConfigService = inject(ConfigService);
  cognitoAuthService: CognitoAuthService = inject(CognitoAuthService);
  localeService: LocaleService = inject(LocaleService);
  authState$: Observable<UserModel | null> = this.store.select(AuthState.getCurrentUser);
  loading$: Observable<boolean> = this.store.select(AuthState.isLoading);

  /**
   * Language switcher is always visible for all users.
   * Le sélecteur de langue est toujours visible pour tous les utilisateurs.
   */
  canSwitchLanguage$: Observable<boolean> = this.authState$.pipe(
    map(() => true)
  );

  get locale(): AppLocale { return this.localeService.locale(); }

  switchLocale(target: AppLocale): void {
    this.localeService.switchLocale(target);
  }


  goToMainPage() {
    this.authState$.pipe(take(1)).subscribe(auth => {
      if (auth?.isStaff) {
        const prefix = this.locale === 'fr' ? '/fr' : '';
        this.router.navigate([`${prefix}/management/select-programs`]);
        return;
      }
    });
  }

  logout() {
    // Clear NGXS state and localStorage; LogoutAction also redirects to the
    // centralized auth-ui logout page when authUiUrl is set in runtime config.
    this.store.dispatch(new LogoutAction());

    const env = this.configService.getEnvironment();

    if (env !== 'dev' && this.cognitoAuthService.isConfigured()) {
      // PKCE direct-Cognito flow — additionally sign out from the Cognito session.
      // LogoutAction already set window.location.href; signOut() fires in parallel
      // but only overrides it if Cognito itself issues a redirect on logout_uri.
      signOut().catch(() => {
        // No-op: LogoutAction already navigated to the SSO logout page.
      });
    } else if (env === 'dev') {
      // Localhost only — redirect to the external login page
      window.location.href = environment.userMgtUrl;
    }
    // SSO mode (env !== 'dev', Cognito not configured): LogoutAction handles redirect.
  }
}
